'use client';

import { AlertTriangleIcon, ArrowBigDownDashIcon, Loader2Icon, RefreshCwIcon, RotateCwIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';

import { requestInstallUpdate, requestManualUpdateCheck, resyncUpdaterStateFromSdk } from '@/app/editor/hooks/useInitApp';
import { globalStore } from '@/app/globalStore';
import { sdk } from '@/app/sdk';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { SDK_RELEASE_URL } from '@/const';
import { cn } from '@/lib/utils';

const getUpdaterLabel = () => {
  if (globalStore.updaterPhase === 'readyToInstall') {
    return 'Restart to Update';
  }
  if (globalStore.updaterPhase === 'available') {
    return 'Update Available';
  }
  if (globalStore.updaterPhase === 'error') {
    return 'Update Failed';
  }
  if (globalStore.updaterPhase === 'checking') {
    return 'Checking';
  }
  return 'Update';
};

/** True when main process reports download bytes/percent even if phase lags as `available`. */
const hasUpdaterProgress = (): boolean => globalStore.updaterProgressPercent !== null;

const getUpdaterIcon = (opts: { showProgressBarInPill: boolean; isStartingDownload: boolean }) => {
  if (globalStore.updaterPhase === 'checking') {
    return <Loader2Icon className="size-3.5 animate-spin" />;
  }
  if (opts.isStartingDownload) {
    return <Loader2Icon className="size-3.5 animate-spin" />;
  }
  if (opts.showProgressBarInPill) {
    return <ArrowBigDownDashIcon className="size-3.5" />;
  }
  if (globalStore.updaterPhase === 'readyToInstall') {
    return <RotateCwIcon className="size-3.5" />;
  }
  if (globalStore.updaterPhase === 'error') {
    return <AlertTriangleIcon className="size-3.5" />;
  }
  return <RefreshCwIcon className="size-3.5" />;
};

const getPillClassName = () => {
  if (globalStore.updaterPhase === 'readyToInstall') {
    return 'bg-indigo-600 hover:bg-indigo-700 text-white';
  }
  if (globalStore.updaterPhase === 'error') {
    return 'text-destructive';
  }
  return '';
};

const getStatusMeta = (): string => {
  const meta: string[] = [];
  const elapsed = globalStore.updaterElapsedSeconds;
  if (elapsed > 0) {
    meta.push(`Working for ${elapsed}s`);
  }
  if (globalStore.updaterRetryAttempt > 0) {
    meta.push(`Retry attempt ${globalStore.updaterRetryAttempt}`);
  }
  return meta.join(' • ');
};

const getStatusMessage = () => {
  if (globalStore.updaterPhase === 'available') {
    if (!hasUpdaterProgress()) {
      return 'Connecting to download. Keep the app open.';
    }
    return 'Downloading update in the background.';
  }
  if (globalStore.updaterPhase === 'downloading') {
    return 'Downloading update in the background.';
  }
  if (globalStore.updaterPhase === 'readyToInstall') {
    return 'Update is ready. Restart to install when convenient.';
  }
  if (globalStore.updaterPhase === 'error') {
    return globalStore.updaterErrorMessage ?? 'Unable to complete the update check.';
  }
  if (globalStore.updaterPhase === 'checking') {
    return 'Checking for updates...';
  }
  return 'No update activity.';
};

const getUpdaterAriaLabel = (
  progressValue: number | undefined,
  opts: { showProgressBarInPill: boolean; isStartingDownload: boolean }
) => {
  if (opts.isStartingDownload) {
    return 'Starting download';
  }
  if (opts.showProgressBarInPill) {
    if (progressValue === undefined) {
      return 'Downloading update';
    }
    return `Downloading update, ${Math.round(progressValue)} percent complete`;
  }
  return 'Application update status';
};

export const TitleBarUpdaterButton = observer(() => {
  if (!globalStore.showUpdaterPill) {
    return null;
  }

  const progressValue = globalStore.updaterProgressPercent === null
    ? undefined
    : Math.max(0, Math.min(100, globalStore.updaterProgressPercent));

  const phase = globalStore.updaterPhase;
  const statusMeta = getStatusMeta();
  const showProgressBarInPill =
    phase === 'downloading'
    || (phase === 'available' && hasUpdaterProgress());
  const isStartingDownload = phase === 'available' && !hasUpdaterProgress();

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="titleBar"
          size="titleBar"
          aria-label={getUpdaterAriaLabel(progressValue, { showProgressBarInPill, isStartingDownload })}
          className={cn('h-7 px-2 titlebarControls', getPillClassName())}
          tooltip="App Update Status"
        >
          {getUpdaterIcon({ showProgressBarInPill, isStartingDownload })}
          {showProgressBarInPill ? (
            progressValue === undefined ? (
              <div
                className="h-1.5 min-w-14 max-w-20 flex-1 rounded-full bg-primary/30 animate-pulse"
                aria-hidden
              />
            ) : (
              <Progress
                value={progressValue}
                className="h-1.5 min-w-14 max-w-20 flex-1"
                aria-hidden
              />
            )
          ) : isStartingDownload ? (
            <span className="text-xs font-medium">Starting download...</span>
          ) : (
            <span className="text-xs font-medium">{getUpdaterLabel()}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80">
        <div className="flex flex-col gap-3">
          <div className="space-y-1">
            <div className="text-sm font-medium">App Update</div>
            <div className="text-xs text-muted-foreground">{getStatusMessage()}</div>
            {statusMeta && (
              <div className="text-2xs text-muted-foreground">{statusMeta}</div>
            )}
          </div>

          {(globalStore.updaterPhase === 'available' || globalStore.updaterPhase === 'downloading') && (
            <div className="space-y-2">
              {progressValue === undefined ? (
                <div className="h-2 w-full rounded-full bg-primary/30 animate-pulse" />
              ) : (
                <Progress value={progressValue} />
              )}
              <div className="text-xs text-muted-foreground">
                {progressValue === undefined
                  ? (phase === 'available' ? 'Connecting to download...' : 'Downloading...')
                  : `${Math.round(progressValue)}% downloaded`}
              </div>
            </div>
          )}
          {globalStore.updaterPhase === 'readyToInstall' && (
            <div className="rounded-md border border-blue-500/40 bg-blue-500/10 px-2 py-1.5 text-xs text-blue-200">
              Download complete. Restart now to install.
            </div>

          )}

          <div className="space-y-1 text-xs text-muted-foreground">
            <div>Current Version (sdk): v{globalStore.updaterCurrentVersion || 'unknown'}</div>
            {globalStore.updaterTargetVersion && <div>Update Version (sdk): v{globalStore.updaterTargetVersion}</div>}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {globalStore.updaterPhase === 'error' && (
              <Button type="button" size="xs" onClick={() => requestManualUpdateCheck()}>
                Retry
              </Button>
            )}
            {(globalStore.updaterPhase === 'available' || globalStore.updaterPhase === 'downloading') && (
              <Button type="button" size="xs" onClick={() => {
                void resyncUpdaterStateFromSdk();
              }}>
                Refresh Status
              </Button>
            )}
            <Button
              type="button"
              size="xs"
              onClick={() => {
                void sdk?.os.openUrl(SDK_RELEASE_URL);
              }}
            >
              Release page
            </Button>
            {globalStore.updaterPhase === 'readyToInstall' && (
              <Button type="button" size="xs" variant="primary" className="animate-pulse" onClick={() => requestInstallUpdate()}>
                Restart now
              </Button>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
});

TitleBarUpdaterButton.displayName = 'TitleBarUpdaterButton';
